import { canonicalIdBase } from '@core-mkt/services/schema/schema-id.util';
import { SchemaOrgNode } from '@core-mkt/services/schema/schema-org-node.model';
import { BreadcrumbItem } from './breadcrumb';

function toAbsoluteUrl(url: string | null | undefined, baseUrl: string): string | null {
  if (!url) return null;
  // Only treat genuine http(s) URLs as already-absolute — guards against invalid
  // schemes like `httpx://` leaking into JSON-LD.
  if (/^https?:\/\//i.test(url)) return url;
  return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
}

/**
 * Builds a Schema.org BreadcrumbList node from the page's breadcrumb items.
 * Returns `null` when breadcrumbs are empty or missing.
 */
export function getBreadcrumbSchema(
  breadcrumbs: BreadcrumbItem[] | null | undefined,
  pageUrl: string,
  baseUrl: string,
): SchemaOrgNode | null {
  if (!breadcrumbs || breadcrumbs.length === 0) {
    return null;
  }

  const lastIndex = breadcrumbs.length - 1;
  // Canonical page URL (origin + pathname, no query/hash) so the BreadcrumbList
  // @id and the last crumb's item URL stay stable across coupon/UTM variants.
  const canonicalPageUrl = canonicalIdBase(pageUrl);

  return {
    '@type': 'BreadcrumbList',
    '@id': `${canonicalPageUrl}#breadcrumbs`,
    itemListElement: breadcrumbs.map((item, index) => {
      const absoluteUrl = toAbsoluteUrl(item.breadcrumbLink, baseUrl) ?? (index === lastIndex ? canonicalPageUrl : null);
      const listItem: Record<string, unknown> = {
        '@type': 'ListItem',
        position: index + 1,
        name: item.breadcrumbText,
      };
      if (absoluteUrl !== null) {
        listItem['item'] = absoluteUrl;
      }
      return listItem;
    }),
  };
}
